import { Utils } from '@zaoblako/shared/common/classes/utils';
import type { TimeZoneEnum } from '@zaoblako/shared/common/enums/time.zone.enum';

import striptags from 'striptags';

const STYLE_OR_SCRIPT_PATTERN = /<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi;

/**
 * Get UTC offset values for received date and time zone.
 * An unknown zone or an invalid date yields an empty string.
 *
 * @param dateInput Input date
 * @param timeZone Input time zone
 *
 * @returns UTC offset (`+0300`) or an empty string
 */
const getUtcOffsetForZone = (dateInput: Date | number | string, timeZone: TimeZoneEnum): string => {
  try {
    const date = dateInput ? new Date(dateInput) : new Date();
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const tzString = date.toLocaleString('en-US', { timeZone, timeZoneName: 'longOffset' });
    const match = tzString.match(/GMT([+-]\d+)(?::(\d+))?/);
    if (!match) {
      return '';
    }

    const hours = match[1];
    const minutes = match[2] || '00';
    const formattedHours = hours.startsWith('-')
      ? '-' + Math.abs(parseInt(hours, 10)).toString().padStart(2, '0')
      : '+' + parseInt(hours, 10).toString().padStart(2, '0');
    const formattedMinutes = minutes.padStart(2, '0');

    return `${formattedHours}${formattedMinutes}`;
  } catch {
    return '';
  }
};

/** Drop style and script blocks, then remove the remaining tags. */
const stripHtml = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  return striptags(value.replace(STYLE_OR_SCRIPT_PATTERN, ''));
};

export const JSONATA_UTIL_BINDINGS = {
  getUtcOffsetForZone,
  stripHtml,
  truncate: Utils.truncate,
};
